import KeywordTabChart from "@/components/charts/KeywordTabChart";
import SentimentComparisonChart from "@/components/charts/SentimentComparisonChart";
import SentimentSelfChart from "@/components/charts/SentimentSelfChart";
import MobileTooltip from "@/components/common/MobileTooltip";
import QABrowserModal from "@/components/modals/QABrowserModal";
import ReputationDetailModal from "@/components/modals/ReputationDetailModal";
import { useBetaStatus } from "@/hooks/useBetaStatus";
import { useQuestionList } from "@/hooks/useQuestionList";
import type { BrandRatesItem } from "@/services/api/BrandRates";
import { formatPercent } from "@/utils/format";
import { Grid } from "antd";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
// import NegativeContentSourceTable from '@/components/charts/NegativeContentSourceTable'

interface SentimentPoint {
  day: string;
  positive: number;
  neutral?: number;
  negative: number;
}

import type { BrandMetricsRates } from "@/services/api/brandMetricsRates";
import type { BrandMetricsTrendPoint } from "@/services/api/brandMetricsTrend";
import type {
  ReputationKeyword,
  ReputationKeywordsResponse,
} from "@/services/api/reputation";
import { reputationDetailApi } from "@/services/api/reputationDetail";

interface SentimentAnalysisSectionProps {
  brandId?: string;
  brandName: string;
  title?: string;
  data?: SentimentPoint[];
  rates?: BrandMetricsRates | null; // 新增：品牌指标数据
  trend?: BrandMetricsTrendPoint[] | null; // 新增：趋势数据
  brandComparison?: any; // 新增：竞品对比数据
  brandRatesList?: BrandRatesItem[];
  reputationKeywords?: ReputationKeywordsResponse; // 新增：品牌声誉关键词数据
  categoryId?: string;
  days?: number;
  modelId?: string;
  selectedPlatforms?: string[]; // 新增：选中的平台列表
  onDownload?: () => void; // 新增：下载按钮回调
}

const SentimentAnalysisSection: React.FC<SentimentAnalysisSectionProps> = ({
  title,
  rates, // 新增：品牌指标数据
  trend, // 新增：趋势数据
  brandComparison, // 新增：竞品对比数据
  brandRatesList,
  reputationKeywords, // 新增：品牌声誉关键词数据
  brandId,
  categoryId,
  days = 7,
  modelId = "all",
  selectedPlatforms, // 新增：选中的平台列表
  onDownload, // 新增：下载按钮回调
}) => {
  const { t } = useTranslation();
  const effectiveTitle = title || t("search_detail.sentiment_brand_index");
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const [qaModalVisible, setQaModalVisible] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState<string>("");
  const [selectedAnswer, setSelectedAnswer] = useState<string>("");
  const [selectedModelName, setSelectedModelName] = useState<string>("");
  const [selectedLinksNum, setSelectedLinksNum] = useState<number>(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedTaskId, setSelectedTaskId] = useState<string>("");
  const [repModalOpen, setRepModalOpen] = useState(false);
  const [repModalLoading, setRepModalLoading] = useState(false);
  const [repModalData, setRepModalData] = useState<any>(null);
  const [repModalKeyword, setRepModalKeyword] = useState<string>("");
  const [repModalSentiment, setRepModalSentiment] = useState<
    "positive" | "negative" | undefined
  >();
  const [repModalTaskIds, setRepModalTaskIds] = useState<string[]>([]);
  const [repModalCurrentIndex, setRepModalCurrentIndex] = useState(0);

  // 新增：分页信息状态
  const [repModalPagination, setRepModalPagination] = useState<{
    total: number;
    currentPage: number;
    pageSize: number;
    keywordId?: string;
    metricType?: "positive" | "negative";
  }>({ total: 0, currentPage: 1, pageSize: 1 });

  // 新增：模拟的taskIds状态，用于支持导航按钮显示
  const [repModalMockTaskIds, setRepModalMockTaskIds] = useState<string[]>([]);

  // 基于关键词Tab的用户意图指标类型（正面/负面）
  const [sentimentMetricType, setSentimentMetricType] = useState<
    "positiveMentionRate" | "negativeMentionRate"
  >("positiveMentionRate");

  // 动态问题参数（随情绪Tab切换）
  const questionParams =
    brandId && categoryId
      ? {
          brandId,
          categoryId,
          days: days || 7,
          metricType: sentimentMetricType, // 跟随当前行业情绪词Tab
          modelId: selectedPlatforms?.length ? selectedPlatforms[0] : "all",
        }
      : undefined;

  // 获取问题列表用于模态框导航
  const questionListData = useQuestionList(
    questionParams || {
      brandId: brandId || "",
      categoryId: categoryId || "",
      days: days || 7,
      metricType: sentimentMetricType,
      modelId: selectedPlatforms?.length ? selectedPlatforms[0] : "all",
    }
  );
  const questionList = questionListData.questions.map((q) => q.question);

  // Beta 限制：非 beta（受限）仅允许查看前三条
  const { isBeta } = useBetaStatus();
  const visibleQuestionCount = Math.min(
    questionList.length,
    isBeta ? 3 : questionList.length
  );

  // 计算整体正负面占比用于顶部条形展示
  // - 矩形宽度：使用归一化后的比例（两者和为100%）
  // - 数字显示：使用原始百分比值（不归一化）
  const { positiveNormPct, negativeNormPct, positiveRawPct, negativeRawPct } =
    React.useMemo(() => {
      if (
        rates?.positiveMentionRate !== undefined &&
        rates?.negativeMentionRate !== undefined
      ) {
        const positive = rates.positiveMentionRate;
        const negative = rates.negativeMentionRate;
        const sum = Math.max(1, positive + negative);
        return {
          positiveNormPct: +((positive * 100) / sum).toFixed(1),
          negativeNormPct: +((negative * 100) / sum).toFixed(1),
          positiveRawPct: +positive.toFixed(1),
          negativeRawPct: +negative.toFixed(1),
        };
      }
      return {
        positiveNormPct: 0,
        negativeNormPct: 0,
        positiveRawPct: 0,
        negativeRawPct: 0,
      };
    }, [rates]);

  // 处理声誉关键词数据：将value转换为weight
  const processedPositiveKeywords = React.useMemo(() => {
    if (
      reputationKeywords?.positive &&
      reputationKeywords.positive.length > 0
    ) {
      return reputationKeywords.positive.map((item) => ({
        text: item.name,
        weight: item.value, // 将value转换为weight
        keywordId: item.keywordId, // 添加keywordId
      }));
    }
    return []; // 只使用reputationKeywords数据源，不使用旧的positiveKeywords
  }, [reputationKeywords?.positive]);

  const processedNegativeKeywords = React.useMemo(() => {
    if (
      reputationKeywords?.negative &&
      reputationKeywords.negative.length > 0
    ) {
      return reputationKeywords.negative.map((item) => ({
        text: item.name,
        weight: item.value, // 将value转换为weight
        keywordId: item.keywordId, // 添加keywordId
      }));
    }
    return []; // 只使用reputationKeywords数据源，不使用旧的negativeKeywords
  }, [reputationKeywords?.negative]);

  // name -> taskIds 映射，供点击时查找
  const positiveTaskIdMap = React.useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    (reputationKeywords?.positive || []).forEach((k: ReputationKeyword) => {
      map[k.name] = k.taskIds;
    });
    return map;
  }, [reputationKeywords?.positive]);

  const negativeTaskIdMap = React.useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    (reputationKeywords?.negative || []).forEach((k: ReputationKeyword) => {
      map[k.name] = k.taskIds;
    });
    return map;
  }, [reputationKeywords?.negative]);

  // name -> keywordId 映射，供点击时查找
  const positiveKeywordIdMap = React.useMemo<
    Record<string, string | undefined>
  >(() => {
    const map: Record<string, string | undefined> = {};
    (reputationKeywords?.positive || []).forEach((k: ReputationKeyword) => {
      map[k.name] = k.keywordId;
    });
    return map;
  }, [reputationKeywords?.positive]);

  const negativeKeywordIdMap = React.useMemo<
    Record<string, string | undefined>
  >(() => {
    const map: Record<string, string | undefined> = {};
    (reputationKeywords?.negative || []).forEach((k: ReputationKeyword) => {
      map[k.name] = k.keywordId;
    });
    return map;
  }, [reputationKeywords?.negative]);

  // 获取任务ID列表的辅助函数（保留以保持接口一致性，但不再使用）
  const getTaskIds = (
    name: string,
    metricType: "positive" | "negative"
  ): string[] => {
    // 不再使用taskId
    return [];
  };

  // 获取keywordId的辅助函数（保留以保持接口一致性，但不再使用）
  const getKeywordId = (
    name: string,
    metricType: "positive" | "negative"
  ): string | undefined => {
    // 不再使用这个函数，因为keywordId已经直接传递给handleKeywordClick
    return undefined;
  };

  // 重置模态框状态的辅助函数
  const resetModalState = () => {
    setRepModalData(null);
    setRepModalOpen(false);
    setRepModalLoading(false);
  };

  // 设置模态框状态的辅助函数
  const setModalState = (
    keyword: string,
    sentiment: "positive" | "negative",
    loading: boolean
  ) => {
    setRepModalKeyword(keyword);
    setRepModalSentiment(sentiment);
    setRepModalLoading(loading);
  };

  const handleKeywordClick = async (
    name: string,
    _weight: number,
    keywordId?: string,
    metricType?: "positive" | "negative"
  ) => {
    // 参数验证
    if (!name || !keywordId) {
      console.warn("关键词点击参数无效:", { name, keywordId, metricType });
      return;
    }

    // 确定情绪类型
    let actualMetricType: "positive" | "negative" = metricType || "positive";
    if (!metricType) {
      // 通过keywordId判断是正面还是负面关键词
      if (
        positiveKeywordIdMap &&
        Object.values(positiveKeywordIdMap).includes(keywordId)
      ) {
        actualMetricType = "positive";
      } else if (
        negativeKeywordIdMap &&
        Object.values(negativeKeywordIdMap).includes(keywordId)
      ) {
        actualMetricType = "negative";
      }
    }

    // 只通过keywordId获取数据
    try {
      console.log("关键词点击参数:", {
        name,
        keywordId,
        actualMetricType,
        brandId,
        categoryId,
        days,
        modelId,
      });

      // 设置初始状态
      setModalState(name, actualMetricType, true);
      resetModalState();

      // 构建API参数
      const apiParams = {
        keywordId,
        brandId: brandId || "brand-mock",
        categoryId: categoryId || "category-mock",
        days,
        metricType: actualMetricType,
        modelId,
        page: 1,
        pageSize: 1, // 每页一项以支持单项导航
      };

      // 调用新的API获取详情
      const response =
        await reputationDetailApi.getReputationKeywordDetailByKeywordIdWithPagination(
          apiParams
        );

      if (!response || !response.list || response.list.length === 0) {
        throw new Error(t("search_detail.sentiment_error_keyword"));
      }

      console.log("详情响应:", {
        question: response.list[0].question,
        answerPreview: response.list[0].answer?.slice(0, 80),
        total: response.total,
        page: response.page,
        pageSize: response.pageSize,
      });

      // 设置成功状态
      setRepModalData(response.list[0]);
      setRepModalLoading(false);
      setRepModalOpen(true);

      // 保存分页信息用于导航
      setRepModalPagination({
        total: response.total,
        currentPage: response.page,
        pageSize: response.pageSize,
        keywordId,
        metricType: actualMetricType,
      });

      // 设置当前索引
      setRepModalCurrentIndex(0);

      // 创建模拟的taskIds数组，长度等于总数据量
      setRepModalMockTaskIds(
        Array.from({ length: response.total }, (_, i) => `mock-task-${i}`)
      );
    } catch (error) {
      console.error("获取关键词详情失败:", error);
      resetModalState();
    }
  };

  // 处理声誉详情模态框的上一个问题
  const handleRepPrevQuestion = async () => {
    // 检查是否有分页信息
    if (!repModalPagination.keywordId || repModalPagination.total <= 1) {
      return;
    }

    // 计算上一项的索引
    const prevIndex = repModalCurrentIndex - 1;
    if (prevIndex < 0) {
      return;
    }

    // 计算上一项所在的页和页内索引
    const pageSize = 1; // 每页一项
    const prevPage = Math.floor(prevIndex / pageSize) + 1;
    const itemIndexInPage = prevIndex % pageSize;

    // 加载对应页的数据
    try {
      setRepModalLoading(true);

      const apiParams = {
        keywordId: repModalPagination.keywordId,
        brandId: brandId || "brand-mock",
        categoryId: categoryId || "category-mock",
        days,
        metricType: repModalPagination.metricType || "positive",
        modelId,
        page: prevPage,
        pageSize: pageSize,
      };

      const response =
        await reputationDetailApi.getReputationKeywordDetailByKeywordIdWithPagination(
          apiParams
        );

      if (response && response.list && response.list.length > itemIndexInPage) {
        // 更新数据和分页信息
        setRepModalData(response.list[itemIndexInPage]);
        setRepModalPagination((prev) => ({
          ...prev,
          currentPage: response.page,
        }));
        // 更新当前索引
        setRepModalCurrentIndex(prevIndex);
      }

      setRepModalLoading(false);
    } catch (error) {
      console.error("加载上一项数据失败:", error);
      setRepModalLoading(false);
    }
  };

  // 处理声誉详情模态框的下一个问题
  const handleRepNextQuestion = async () => {
    // 检查是否有分页信息
    if (!repModalPagination.keywordId || repModalPagination.total <= 1) {
      return;
    }

    // 计算下一项的索引
    const nextIndex = repModalCurrentIndex + 1;
    if (nextIndex >= repModalPagination.total) {
      return;
    }

    // 计算下一项所在的页和页内索引
    const pageSize = 1; // 每页一项
    const nextPage = Math.floor(nextIndex / pageSize) + 1;
    const itemIndexInPage = nextIndex % pageSize;

    // 加载对应页的数据
    try {
      setRepModalLoading(true);

      const apiParams = {
        keywordId: repModalPagination.keywordId,
        brandId: brandId || "brand-mock",
        categoryId: categoryId || "category-mock",
        days,
        metricType: repModalPagination.metricType || "positive",
        modelId,
        page: nextPage,
        pageSize: pageSize,
      };

      const response =
        await reputationDetailApi.getReputationKeywordDetailByKeywordIdWithPagination(
          apiParams
        );

      if (response && response.list && response.list.length > itemIndexInPage) {
        // 更新数据和分页信息
        setRepModalData(response.list[itemIndexInPage]);
        setRepModalPagination((prev) => ({
          ...prev,
          currentPage: response.page,
        }));
        // 更新当前索引
        setRepModalCurrentIndex(nextIndex);
      }

      setRepModalLoading(false);
    } catch (error) {
      console.error("加载下一项数据失败:", error);
      setRepModalLoading(false);
    }
  };

  // 根据索引加载问题（保留以保持接口一致性，但不再使用）
  const loadQuestionByIndex = async (index: number) => {
    // 不再使用这个方法
    return;
  };

  const rootRef = React.useRef<HTMLDivElement>(null);
  const [tipMaxWidth, setTipMaxWidth] = React.useState<number>(360);
  const tipAnchorRef = React.useRef<HTMLDivElement>(null);
  const [tipOpen, setTipOpen] = React.useState<boolean>(false);
  React.useLayoutEffect(() => {
    const calc = () => {
      const container = rootRef.current;
      const anchor = tipAnchorRef.current;
      if (!container || !anchor) return;
      const cr = container.getBoundingClientRect();
      const ar = anchor.getBoundingClientRect();
      const cs = window.getComputedStyle(container);
      const pr = parseFloat(cs.paddingRight || "0");
      const br = parseFloat(cs.borderRightWidth || "0");
      const sectionRight = cr.right - pr - br;
      const viewportRight = window.innerWidth - 8;
      const rightEdge = Math.min(sectionRight, viewportRight);
      const available = Math.max(0, Math.floor(rightEdge - ar.left - 4));
      setTipMaxWidth(available);
    };
    const ro = new ResizeObserver(calc);
    if (rootRef.current) ro.observe(rootRef.current);
    calc();
    window.addEventListener("scroll", calc, { passive: true });
    window.addEventListener("resize", calc);
    return () => {
      ro.disconnect();
      window.removeEventListener("scroll", calc as any);
      window.removeEventListener("resize", calc as any);
    };
  }, []);
  React.useEffect(() => {
    const close = () => setTipOpen(false);
    const container = rootRef.current;
    window.addEventListener("scroll", close, { passive: true });
    container?.addEventListener("scroll", close, { passive: true } as any);
    return () => {
      window.removeEventListener("scroll", close as any);
      container?.removeEventListener("scroll", close as any);
    };
  }, []);

  // 处理问题点击事件
  const handleQuestionClick = async (question: string, index: number) => {
    setSelectedQuestion(question);
    setCurrentQuestionIndex(index);
    setQaModalVisible(true);
    try {
      // 确保questionListData已经加载完成
      if (
        !questionListData ||
        !questionListData.questions ||
        questionListData.questions.length === 0
      ) {
        console.warn(
          "[SentimentAnalysisSection] questionListData is not ready yet"
        );
        setSelectedAnswer("");
        setSelectedTaskId("");
        return;
      }

      // 检查索引是否有效
      if (index < 0 || index >= questionListData.questions.length) {
        console.warn(
          "[SentimentAnalysisSection] 无效的索引:",
          index,
          "问题总数:",
          questionListData.questions.length
        );
        setSelectedAnswer("");
        setSelectedTaskId("");
        return;
      }

      const item: any = questionListData.questions[index];
      console.log("🎯 点击问题item:", item);
      const taskId = item?.taskId;
      if (!taskId) {
        setSelectedAnswer("");
        setSelectedTaskId("");
        return;
      }
      setSelectedTaskId(taskId);
      const data = await reputationDetailApi.getReputationKeywordDetail({
        taskId,
        brandId: brandId || "brand-mock",
        categoryId: categoryId || "category-mock",
        days,
        metricType: sentimentMetricType,
        modelId: selectedPlatforms?.length ? selectedPlatforms[0] : "all",
      });
      setSelectedAnswer(data?.answer || "");
      setSelectedModelName(data?.modelName || "");
      setSelectedLinksNum(data?.linksNum || 0);
    } catch (e) {
      console.error("[SentimentAnalysisSection] 加载回答失败:", e);
      setSelectedAnswer("");
      setSelectedTaskId("");
    }
  };

  // 处理上一个问题
  const handlePrevQuestion = async () => {
    if (currentQuestionIndex > 0) {
      const newIndex = currentQuestionIndex - 1;
      setCurrentQuestionIndex(newIndex);
      setSelectedQuestion(questionList[newIndex]);
      try {
        // 确保questionListData已经加载完成
        if (
          !questionListData ||
          !questionListData.questions ||
          questionListData.questions.length === 0
        ) {
          console.warn(
            "[SentimentAnalysisSection] questionListData is not ready yet"
          );
          setSelectedAnswer("");
          setSelectedTaskId("");
          return;
        }

        const item: any = questionListData.questions[newIndex];
        const taskId = item?.taskId;
        if (!taskId) {
          setSelectedAnswer("");
          setSelectedTaskId("");
          return;
        }
        // 更新taskId状态，确保CitelistModal能获取正确的taskId
        setSelectedTaskId(taskId);
        const data = await reputationDetailApi.getReputationKeywordDetail({
          taskId,
          brandId: brandId || "brand-mock",
          categoryId: categoryId || "category-mock",
          days,
          metricType: sentimentMetricType,
          modelId: selectedPlatforms?.length ? selectedPlatforms[0] : "all",
        });
        setSelectedAnswer(data?.answer || "");
        setSelectedModelName(data?.modelName || "");
        setSelectedLinksNum(data?.linksNum || 0);
      } catch (e) {
        console.error("[SentimentAnalysisSection] 加载回答失败:", e);
        setSelectedAnswer("");
        setSelectedTaskId("");
      }
    }
  };

  // 处理下一个问题
  const handleNextQuestion = async () => {
    console.log("[handleNextQuestion] 开始执行");
    // 确保questionListData已经加载完成
    if (
      !questionListData ||
      !questionListData.questions ||
      questionListData.questions.length === 0
    ) {
      console.warn(
        "[SentimentAnalysisSection] questionListData is not ready yet"
      );
      return;
    }

    const maxIndex =
      Math.min(questionListData.questions.length, visibleQuestionCount) - 1;
    console.log(
      "[handleNextQuestion] currentQuestionIndex:",
      currentQuestionIndex,
      "maxIndex:",
      maxIndex
    );
    if (currentQuestionIndex < maxIndex) {
      const newIndex = currentQuestionIndex + 1;
      setCurrentQuestionIndex(newIndex);
      setSelectedQuestion(questionList[newIndex]);
      try {
        const item: any = questionListData.questions[newIndex];
        const taskId = item?.taskId;
        console.log("[handleNextQuestion] 新的taskId:", taskId);
        if (!taskId) {
          setSelectedAnswer("");
          setSelectedTaskId("");
          return;
        }
        // 更新taskId状态，确保CitelistModal能获取正确的taskId
        console.log("[handleNextQuestion] 调用setSelectedTaskId:", taskId);
        setSelectedTaskId(taskId);
        const data = await reputationDetailApi.getReputationKeywordDetail({
          taskId,
          brandId: brandId || "brand-mock",
          categoryId: categoryId || "category-mock",
          days,
          metricType: sentimentMetricType,
          modelId: selectedPlatforms?.length ? selectedPlatforms[0] : "all",
        });
        setSelectedAnswer(data?.answer || "");
        setSelectedModelName(data?.modelName || "");
        setSelectedLinksNum(data?.linksNum || 0);
      } catch (e) {
        console.error("[SentimentAnalysisSection] 加载回答失败:", e);
        setSelectedAnswer("");
        setSelectedTaskId("");
      }
    }
  };

  return (
    <div
      ref={rootRef}
      style={{
        marginTop: 16,
        background: "#ffffff",
        borderRadius: 6,
        padding: isMobile ? 10 : 20,
        position: "relative",
        overflow: "hidden",
        boxShadow: "0 2px 8px -2px rgba(0,0,0,.08)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 12,
        }}
      >
        <span style={{ color: "#000000", fontSize: 18, fontWeight: "normal" }}>
          {effectiveTitle}
        </span>
        <MobileTooltip
          content={
            <>
              {t("api.metric_positive_sentiment_tooltip")}
              <br />
              <br />
              {t("api.metric_negative_sentiment_tooltip")}
              <br />
              <br />
              {t("api.metric_tooltip_note")}
            </>
          }
          title={
            effectiveTitle + " - " + t("search_detail.insight_metric_desc")
          }
        />
      </div>
      {/* 占比条 - 优化排版 */}
      <div style={{ marginBottom: 16 }}>
        {/* 标签行：一行展示 */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginBottom: 12,
          }}
        >
          <span style={{ fontSize: 16, fontWeight: "normal" }}>
            <span style={{ color: "#21C4B8" }}>
              {t("search_detail.insight_metric_positive")}
            </span>
            <span style={{ color: "rgb(148, 163, 184)", margin: "0 12px" }}>
              VS
            </span>
            <span style={{ color: "#ef6b5f" }}>
              {t("search_detail.insight_metric_negative")}
            </span>
          </span>
        </div>
        {/* 数字对比行 */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 40,
            marginBottom: 12,
          }}
        >
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 32, fontWeight: "bold", color: "#21C4B8" }}>
              {formatPercent(positiveRawPct, 1)}
            </div>
          </div>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 32, fontWeight: "bold", color: "#ef6b5f" }}>
              {formatPercent(negativeRawPct, 1)}
            </div>
          </div>
        </div>
        {/* 条形图 - 一行展示 */}
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div
            style={{
              flex: 1,
              height: 20,
              borderRadius: 20,
              background: "#f1f5f9",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                width: `${positiveRawPct}%`,
                height: "100%",
                background: "#21C4B8",
              }}
            />
          </div>
          <div
            style={{
              flex: 1,
              height: 20,
              borderRadius: 20,
              background: "#f1f5f9",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                width: `${negativeRawPct}%`,
                height: "100%",
                background: "#ef6b5f",
              }}
            />
          </div>
        </div>
      </div>
      {/* 图表容器：参考CompetitorOverviewSection的单列全宽布局 */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 16,
          marginTop: 0,
        }}
      >
        {/* 折线图 */}
        <div style={{ width: "100%", maxWidth: "100%" }}>
          {/* 外部标题：情绪趋势 */}
          <div style={{ marginBottom: 12 }}>
            <span
              style={{
                whiteSpace: "nowrap",
                color: "rgb(148, 163, 184)",
                fontSize: 16,
                fontWeight: "normal",
              }}
            >
              {t("search_detail.sentiment_trend")}
            </span>
          </div>
          <div style={{ marginTop: 8, width: "100%", maxWidth: "100%" }}>
            <SentimentSelfChart trend={trend} height={400} />
          </div>
        </div>

        {/* 情绪指数排行榜 */}
        <div style={{ width: "100%" }}>
          <SentimentComparisonChart
            brandComparison={brandComparison}
            brandRatesList={brandRatesList}
            height={360}
            title={t("search_detail.sentiment_ranking")}
            onDownload={onDownload}
          />
        </div>

        {/* 关键词区：Tab 切换布局 */}
        <div style={{ marginTop: 16 }}>
          <KeywordTabChart
            positiveData={processedPositiveKeywords}
            negativeData={processedNegativeKeywords}
            onPositiveKeywordClick={(name, weight, keywordId) =>
              handleKeywordClick(name, weight, keywordId, "positive")
            }
            onNegativeKeywordClick={(name, weight, keywordId) =>
              handleKeywordClick(name, weight, keywordId, "negative")
            }
            onTabChange={(tab) =>
              setSentimentMetricType(
                tab === "positive"
                  ? "positiveMentionRate"
                  : "negativeMentionRate"
              )
            }
          />
        </div>

        {/* 用户意图表格 */}
        {/* <div style={{ width: '100%' }}>
        <UserIntentTable
          questionParams={questionParams}
          onDownload={onDownload}
          onQuestionClick={handleQuestionClick}
        />
      </div> */}

        {/* 负面内容引用来源 - 已隐藏 */}
        {/* <div style={{ marginTop: 24 }}>
        <NegativeContentSourceTable />
      </div> */}
      </div>

      {/* QA Browser Modal */}
      <QABrowserModal
        open={qaModalVisible}
        onClose={() => setQaModalVisible(false)}
        question={selectedQuestion}
        answer={selectedAnswer}
        modelName={selectedModelName}
        linksNum={selectedLinksNum}
        taskId={selectedTaskId}
        brandId={brandId}
        categoryId={categoryId}
        days={days}
        metricType={sentimentMetricType}
        modelId={selectedPlatforms?.length ? selectedPlatforms[0] : "all"}
        onPrevQuestion={handlePrevQuestion}
        onNextQuestion={handleNextQuestion}
        hasPrevQuestion={currentQuestionIndex > 0}
        hasNextQuestion={currentQuestionIndex < visibleQuestionCount - 1}
      />

      {/* Reputation Detail Modal */}
      <ReputationDetailModal
        open={repModalOpen}
        onClose={() => setRepModalOpen(false)}
        loading={repModalLoading}
        data={repModalData}
        keyword={repModalKeyword}
        sentiment={repModalSentiment}
        taskIds={repModalMockTaskIds}
        currentIndex={repModalCurrentIndex}
        total={repModalPagination.total}
        onPrevQuestion={handleRepPrevQuestion}
        onNextQuestion={handleRepNextQuestion}
      />
    </div>
  );
};

export default SentimentAnalysisSection;
